import type { Product, ProductImage } from '@scayle/storefront-nuxt'
import {
  getImageAssetType,
  getSortedProductImages,
} from '#layers/base/utils/image'

/**
 * Asset type of the catalog image (Katalogbild).
 */
const CATALOG_ASSET_TYPE = '_kb'

/**
 * Moves the catalog image (`_kb`) to the front of an already-sorted product
 * image list, preserving the relative order of every other image.
 *
 * Used on the MAD PDP galleries to roll out the MAD-T46 winner: the catalog
 * image is shown first instead of last (ECOM-2645 desktop, ECOM-2643 mobile).
 * It is a no-op when the list has no catalog image or the catalog image is
 * already first, so products without a `_kb` asset keep their existing order.
 *
 * MAD-only by construction: the shared sort in `layers/base/utils/image.ts`
 * is untouched, so AGS gallery ordering is unaffected.
 */
export const moveCatalogImageFirst = (
  images: ProductImage[],
): ProductImage[] => {
  const index = images.findIndex(
    (image) => getImageAssetType(image) === CATALOG_ASSET_TYPE,
  )

  if (index <= 0) {
    return images
  }

  const catalogImage = images[index]
  if (!catalogImage) {
    return images
  }

  return [catalogImage, ...images.slice(0, index), ...images.slice(index + 1)]
}

/**
 * The canonical MAD PDP gallery ordering: the shared asset-type sort with the
 * catalog image (`_kb`) moved to the front (MAD-T46 winner — ECOM-2645 desktop,
 * ECOM-2643 mobile).
 *
 * This is the single source of truth for gallery order on MAD PDPs. Both the
 * on-page gallery and the fullscreen zoom modal must derive their image list
 * (and any emitted image index) from this helper with the SAME arguments, so
 * the perceived click position and the modal's slide/pagination stay in sync
 * (ECOM-2643, comment 63550). Mirroring the composition inline in multiple
 * components silently reintroduces that ordering drift.
 */
export const getMadPdpGalleryImages = (
  images: ProductImage[],
  product: Product,
  isMobile = false,
): ProductImage[] =>
  moveCatalogImageFirst(getSortedProductImages(images, product, isMobile))
